import { RefObject, UIEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { NotificationInterface } from "../../models/notification";
import { useNotificationContext } from "../../NotificationContext";
import { SortOrder } from "../../service/notification/Types";
import { searchNotifications } from "../../service/query/QueryService.full";
import { SearchNotificationsRequest } from "../../service/query/Types";
import { Status } from "../../service/Shared";
import { mapNotificationResponse } from "../../utils/notification";

export type { SortOrder };

export interface NotificationItem extends NotificationInterface {
  dismissed: boolean;
}

interface UseNotificationPanelProps {
  show: boolean;
}

const PAGE_SIZE = 10;
const SCROLL_TOLERANCE = 5;

const toItems = (incoming: NotificationInterface[]): NotificationItem[] =>
  incoming.map((n) => ({ ...n, dismissed: n.isDismissed }));

const buildRequest = (
  nextSortOrder: SortOrder,
  nextShowDismissed: boolean,
  afterCursor: string | null
): SearchNotificationsRequest => {
  const direction = nextSortOrder === "newest" ? ("desc" as const) : ("asc" as const);
  return {
    paging: { afterCursor, beforeCursor: null, limit: PAGE_SIZE },
    filter: {
      results: {
        deliveryType: { operator: "neq" as const, value: "Email" },
        ...(nextShowDismissed ? {} : { isDismissed: { operator: "eq" as const, value: false } }),
      },
    },
    sort: [{ key: "results.createdAt" as const, direction }],
  };
};

export const useNotificationPanel = ({
  show,
}: UseNotificationPanelProps): {
  items: NotificationItem[];
  hasUnread: boolean;
  showDismissed: boolean;
  sortOrder: SortOrder;
  fetchError: boolean;
  isFetchingMore: boolean;
  contentAreaRef: RefObject<HTMLDivElement>;
  handleDismiss: (uuid: string) => Promise<void>;
  handleNotificationClick: (uuid: string) => Promise<void>;
  handleMarkAllRead: () => Promise<void>;
  handleToggleShowDismissed: (value: boolean) => void;
  handleToggleSortOrder: () => void;
  onScrollContent: (e: UIEvent<HTMLElement>) => void;
} => {
  const { markNotificationRead, navigateToNotification, markAllNotificationsRead, dismissNotification } =
    useNotificationContext();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [showDismissed, setShowDismissed] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [fetchError, setFetchError] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const contentAreaRef = useRef<HTMLDivElement>(null);

  const fetchPanelData = (nextSortOrder: SortOrder, nextShowDismissed: boolean): void => {
    searchNotifications(buildRequest(nextSortOrder, nextShowDismissed, null))
      .then((response) => {
        if (response.status === Status.Success && response.data) {
          const { results, paging, aspects } = response.data;
          setItems(toItems(results.map(mapNotificationResponse)));
          setEndCursor(paging.endCursor ?? null);
          setHasNextPage(paging.hasNextPage);
          setHasUnread(aspects.hasUnread);
          setScrollTop(0);
          setFetchError(false);
        }
      })
      .catch(() => setFetchError(true));
  };

  useEffect(() => {
    if (!show) return;
    fetchPanelData(sortOrder, showDismissed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadMore = useCallback((): void => {
    if (!hasNextPage || isFetchingMore) return;
    setIsFetchingMore(true);
    searchNotifications(buildRequest(sortOrder, showDismissed, endCursor))
      .then((response) => {
        if (response.status === Status.Success && response.data) {
          const { results, paging, aspects } = response.data;
          setItems((prev) => [...prev, ...toItems(results.map(mapNotificationResponse))]);
          setEndCursor(paging.endCursor ?? null);
          setHasNextPage(paging.hasNextPage);
          setHasUnread(aspects.hasUnread);
          setFetchError(false);
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setIsFetchingMore(false));
  }, [hasNextPage, isFetchingMore, sortOrder, showDismissed, endCursor]);

  // If the content doesn't fill the container (e.g. zoomed out), keep loading until it does.
  // `show` is included so this also fires when the panel first opens.
  useLayoutEffect(() => {
    const el = contentAreaRef.current;
    if (!show || !el || !hasNextPage || isFetchingMore) return;
    if (el.scrollHeight <= el.clientHeight) {
      loadMore();
    }
  }, [show, items, hasNextPage, isFetchingMore, loadMore]);

  const onScrollContent = useCallback(
    (e: UIEvent<HTMLElement>): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const target = e.target as any;
      if (
        target.scrollTop > SCROLL_TOLERANCE + scrollTop &&
        target.scrollTop + SCROLL_TOLERANCE + target.clientHeight >= target.scrollHeight
      ) {
        setScrollTop(target.scrollTop);
        loadMore();
      }
    },
    [scrollTop, loadMore]
  );

  const handleDismiss = async (uuid: string): Promise<void> => {
    const wasUnread = items.find((item) => item.uuid === uuid)?.unread ?? false;
    setItems((prev) => prev.map((item) => (item.uuid === uuid ? { ...item, dismissed: true } : item)));
    // The context owns the bell's unread count and the underlying write.
    await dismissNotification(uuid, wasUnread);
  };

  const handleNotificationClick = async (uuid: string): Promise<void> => {
    const item = items.find((n) => n.uuid === uuid);
    if (!item) return;
    // Always navigate to the notification's target, so it can be opened more than once.
    navigateToNotification(item);
    // Only spend a mark-read write when there's actually an unread state to clear.
    if (item.unread) {
      setItems((prev) => prev.map((n) => (n.uuid === uuid ? { ...n, unread: false } : n)));
      await markNotificationRead(uuid);
    }
  };

  const handleMarkAllRead = async (): Promise<void> => {
    setItems((prev) => prev.map((item) => ({ ...item, unread: false })));
    setHasUnread(false);
    await markAllNotificationsRead();
  };

  const handleToggleShowDismissed = (value: boolean): void => {
    setShowDismissed(value);
    fetchPanelData(sortOrder, value);
  };

  const handleToggleSortOrder = (): void => {
    const newOrder: SortOrder = sortOrder === "newest" ? "oldest" : "newest";
    setSortOrder(newOrder);
    fetchPanelData(newOrder, showDismissed);
  };

  const visible = showDismissed ? items : items.filter((item) => !item.dismissed);

  return {
    items: visible,
    hasUnread,
    showDismissed,
    sortOrder,
    fetchError,
    isFetchingMore,
    contentAreaRef,
    handleDismiss,
    handleNotificationClick,
    handleMarkAllRead,
    handleToggleShowDismissed,
    handleToggleSortOrder,
    onScrollContent,
  };
};
